import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'underscore';
import { log } from 'log';
import { Icon } from '@core/js/components/common/Icon';
import { useGameImageUrls } from '@core/js/hooks/games';
import { useNavigate } from '@theme/js/hooks/navigate';
import { componentService } from '@core/js/services/component.service';
import { searchFlex } from '@core/js/utils/search.util';
import { Image } from '@core/js/components/common/Image';
import Overlay from '@core/js/components/common/Overlay';
import { InputShallow } from '@theme/js/components/common/Fields/Input';
import withGameLaunch from '@core/js/components/common/Game/withGameLaunch';
import { useGameConfigurationSelector, useDeviceSelector, useAppSelector } from '@core/js/store/hooks';
import { dispatcher } from 'dispatcher';
import { mapProps, onlyUpdateForKeys, shouldUpdate, withHandlers, withProps } from 'recompose';
import {
    compose,
    equals,
    intersection,
    keys,
    map,
    omit,
    pick,
    pipe,
    prop,
    propOr,
    uniq,
    unnest,
    find,
    propEq,
    includes
} from 'ramda';
import { useAssetRedirectData } from '@theme/js/components/common/Game/hooks/useAssetRedirectData';
import { useTranslations } from '@core/js/hooks/i18n';

import { setSearchActiveAction } from './actions';
import { trackGameSearchChange } from '@core/js/components/main/Games/trackingApi';

import style from './GlobalSearch.scss';

export const GameItem = withGameLaunch(
    ({ name: gameName, launchGame, handleBlur, feedName: gameFeed, matchesStart, matchesEnd, code, isActive }) => {
        const imageUrls = useGameImageUrls({ code });
        const { isArticleIdPathPresent, linkToPage } = useAssetRedirectData(code);
        const navigate = useNavigate();

        /* Lugano changes */
        const combinedFunction = useCallback((event) => {
            event.stopPropagation();
            event.preventDefault();

            isArticleIdPathPresent
                ? (() => {
                    handleBlur();
                    navigate(linkToPage);
                })()
                : (() => {
                    launchGame({ container: 'global', gameFeed, gameName });
                    handleBlur();
                })();
        }, [isArticleIdPathPresent, handleBlur, navigate, linkToPage, launchGame, gameFeed, gameName]);
        /* Lugano changes */

        return (
            <li>
                <span className={style.item} data-game-code={code} onClick={combinedFunction} tabIndex={isActive ? 0 : -1}> {/* Lugano changes */}
                    <Image src={imageUrls} alt={gameName} className={style.image} />
                    <span dangerouslySetInnerHTML={{
                        __html: highlightMatches({ value: gameName, matchesStart, matchesEnd })
                    }} />
                </span>
            </li>
        );
    }
);

export const highlightStart = `<span class="${style.searchQuery}">`;
export const highlightEnd = '</span>';

export const highlightMatches = ({ value, matchesStart, matchesEnd }) => {
    if (matchesStart.length === 0) {
        return value;
    }

    return value.replace(/(?=.)/g, (char, index) => {
        if (matchesStart.includes(index)) {
            return highlightStart;
        }

        if (matchesEnd.includes(index)) {
            return highlightEnd;
        }

        return '';
    });
};

export const SearchBare = ({
    query,
    result,
    isActive,
    onBlur,
    onFocus,
    handleBlur,
    onChange,
    handleBack,
    handleReset,
    setSearchContainerRef,
    setSearchInputRef,
    setFocus,
    handleEscapeBlur,
    className = '',
    activeClassName = '',
    feedName, /* Lugano changes */
    isMobile, /* Lugano changes */
    setIsActive, /* Lugano changes */
    listenGlobalSearchEvent, /* Lugano changes */
    os /* Lugano changes */
}) => {
    const t = useTranslations('playtech.mobile.system.global-search.'); /* Lugano changes */
    const activeCls = isActive ? `${style.active} ${activeClassName}` : '';
    const clsName = `${style.searchContainer} ${className} ${activeCls}`;
    const inputRef = useRef(null); /* Lugano changes */
    const isIphone = isMobile && os === 'ios'; /* Lugano changes */

    /* Lugano changes */
    useEffect(() => {
        document.addEventListener('hotKey:escape', handleEscapeBlur);

        if (listenGlobalSearchEvent) {
            dispatcher.listen('game:open:globalSearch', () => {
                isIphone && setFocus();
                setIsActive(true);
                setTimeout(() => {
                    setFocus();
                }, 100);
            });
        }

        return () => {
            document.removeEventListener('hotKey:escape', handleEscapeBlur);
            dispatcher.removeListener('game:open:globalSearch');
        };
    }, [handleEscapeBlur, setFocus, setIsActive, listenGlobalSearchEvent, isIphone]);

    useEffect(() => {
        if (inputRef.current) {
            setSearchInputRef(inputRef.current);
        }
    }, [inputRef, setSearchInputRef]);

    const isActiveResult = isActive && result.length > 0;
    const isActiveQuery = isActive && query.length > 0;
    const clasSNameForSearchInput = `${!isMobile && isActiveResult ? style.inputResultActive : ''} ${!isMobile && isActiveQuery ? style.inputQueryActive : ''} ${style.input}`;
    /* Lugano changes */

    return <Fragment>
        {isMobile && isActive && <Overlay className={style.overlay} />}
        <div className={clsName} ref={setSearchContainerRef}>
            <div className={style.inputWrapper}>
                <div className={style.inputContainer}>
                    {!isMobile
                        && <div className={style.searchIcon}>
                            {isActiveQuery
                                ? <Icon type="close_svg"
                                        onClick={handleReset}
                                        className={`${style.icon} ${style.close}`}
                                />
                                : <Icon type="search"
                                        className={`${style.icon} ${style.search}`}
                                        onClick={setFocus}/>
                            }
                         </div>
                    }

                    {isMobile && (
                        <>
                            <Icon type="search"
                                  className={`${style.icon} ${style.search}`}
                                  onClick={setFocus}
                            />
                            <Icon type="back_arrow"
                                  onClick={handleBack}
                                  className={`${style.icon} ${style.back}`}
                            />
                        </>
                    )}
                    <InputShallow placeholder={t('search.short')}
                                  value={query}
                                  onBlur={onBlur}
                                  onFocus={onFocus}
                                  onChange={onChange}
                                  ref={inputRef}
                                  className={clasSNameForSearchInput}
                    />
                    {isMobile && isActiveQuery && (
                        <Icon type="close_svg"
                              onClick={handleReset}
                              className={`${style.icon} ${style.close}`}
                        />
                    )}
                    {isActiveResult && (
                        <div className={style.counter}>
                            {result.length} {t(`${result.length > 1 ? 'items' : 'item'}`)}
                        </div>
                    )}
                </div>
            </div>

            <ul className={`${style.list} ${isActiveResult ? style.shown : ''}`}>
                {result.map(({ id, value, matchesStart, matchesEnd }) => (
                    <GameItem
                        key={id}
                        code={id}
                        name={value}
                        isActive={isActive}
                        handleBlur={handleBlur}
                        matchesEnd={matchesEnd}
                        matchesStart={matchesStart}
                        feedName={feedName}
                    />
                ))}
            </ul>
        </div>
    </Fragment>;
};

export const setSearchGamesFn = ({ games, setSearchGames }) => {
    const searchSource = games.map(game => ({
        id: game.code,
        value: game.name,
        source: game
    }));
    setSearchGames(() => searchFlex(searchSource));
};

export const GlobalSearch = compose(
    onlyUpdateForKeys(['query', 'result', 'isActive']),
    withProps(() => useDeviceSelector(pick(['isMobile', 'isLandscape', 'os', 'isTablet', 'osVersion']))),
    withProps((props) => {
        const { gamesfeedname } = props;
        const { oldGamesFeeds, dynamicTags } = useAppSelector(pick(['oldGamesFeeds', 'dynamicTags']));
        const games = useGameConfigurationSelector(prop('games'));
        const [searchGames, setSearchGames] = useState(null);
        const [query, setQuery] = useState('');
        const [result, setResult] = useState([]);
        const [isActive, setIsActive] = useState(false);
        const feedsNames = map(prop('name'), oldGamesFeeds);
        const searchFeedNameInReplacer = includes(gamesfeedname, feedsNames);
        const searchFeedNameInDTAG = includes(dynamicTags.DTAG_GLOBAL_SEARCH_FEED, feedsNames);
        const feedName = gamesfeedname && searchFeedNameInReplacer
            ? gamesfeedname
            : searchFeedNameInDTAG && dynamicTags.DTAG_GLOBAL_SEARCH_FEED;

        const [stateCategories, setStateCategories] = useState([]);

        useEffect(() => {
            if (feedsNames?.length) {
                if (feedName) {
                    const { categories } = feedName && oldGamesFeeds.length > 0
                        ? pick(['categories'], find(propEq('name', feedName))(oldGamesFeeds))
                        : { categories : [] };
                    setStateCategories(categories);
                } else {
                    log('error', 'Unable to parse feedName configuration from Global Search Replacer or Dynamic Tags');
                }
            }

        }, [feedName, feedsNames, oldGamesFeeds]);

        const gameCodes = useMemo(
            () => pipe(
                map(propOr([], 'gameCodes')),
                unnest,
                uniq
            )(stateCategories),
            [stateCategories]
        );

        useEffect(() => {
            const availableGames = intersection(keys(games), gameCodes);
            setSearchGamesFn({
                games: availableGames.map((code) => {
                    const { name } = games[code];
                    return {
                        code,
                        name
                    };
                }),
                setSearchGames
            });
        }, [games, gameCodes]);

        return {
            searchGames,
            query, setQuery,
            result, setResult,
            isActive, setIsActive,
            feedName
        };
    }),
    shouldUpdate((props, { query, result, isActive, gameCodes }) =>
        props.query !== query ||
        props.result !== result ||
        props.isActive !== isActive ||
        !equals(props.gameCodes, gameCodes)
    ),
    withHandlers(({
        feedName,
        setIsActive,
        setQuery,
        setResult
    }) => {
        let containerRef;
        let inputRef;
        const setInputFocus = () => inputRef.focus();
        const setInputBlur = () => inputRef && inputRef.blur();

        const setIsActiveWrapper = (isActive) => {
            document.documentElement.classList.toggle(style.hideNavigationBar, isActive);
            setIsActive(isActive);
        };

        const handleBlur = ({ target }) => {
            if (!containerRef.contains(target) || target === containerRef) {
                setIsActiveWrapper(false);
                window.removeEventListener('click', handleBlur);
                setQuery('');
                setResult([]);
            }
        };

        const handleExternalAPI = debounce((search) => {
            trackGameSearchChange('global', feedName, search);
        }, 1500);

        const setSearchActive = isActive => dispatcher.dispatch(setSearchActiveAction(isActive));

        return {
            setSearchContainerRef: () => ref => containerRef = ref,
            setSearchInputRef: () => ref => inputRef = ref,
            setFocus: () => () => setInputFocus(),
            handleBlur: () => () => {
                setIsActiveWrapper(false);
                window.removeEventListener('click', handleBlur);
                setSearchActive(false);
                setQuery('');
                setResult([]);
            },
            handleEscapeBlur: () => () => {
                setIsActiveWrapper(false);
                window.removeEventListener('click', handleBlur);
                setSearchActive(false);
                setInputBlur();
                setQuery('');
                setResult([]);
            },
            onBlur: () => () => setSearchActive(false),
            onFocus: ({ isLandscape, os, isTablet, osVersion }) => () => {
                setIsActiveWrapper(true);

                setTimeout(() => window.addEventListener('click', handleBlur));

                const isNeedDelay = os === 'ios' && (isTablet && isLandscape || parseFloat(osVersion) >= 13);

                if (isNeedDelay) {
                    const observer = new ResizeObserver((entries) => {
                        const isActive = entries[0].target.className.includes(style.active);

                        if (isActive) {
                            setTimeout(() => scrollTo({
                                top: -1,
                                left: 0
                            }), 50);
                        } else {
                            observer.unobserve(containerRef);
                        }
                    });

                    observer.observe(containerRef);
                }

                setSearchActive(true);
            },
            onChange: ({ searchGames }) => (event) => {
                const { value } = event.target;

                setQuery(value);

                if (value.length < 3) {
                    setResult([]);
                } else {
                    setResult(searchGames(value.trim()));
                    handleExternalAPI(value.trim());
                }
            },
            handleReset: () => (event) => {
                event.stopPropagation();
                setQuery('');
                setResult([]);
                setInputFocus();
            },
            handleBack: () => (event) => {
                event.stopPropagation();
                setQuery('');
                setResult([]);
                setIsActiveWrapper(false);
                removeEventListener('click', handleBlur);
            }
        };
    }),
    mapProps(omit([
        'dispatch',
        'games',
        'gameCodes',
        'setQuery',
        'setResult',
        'searchGames',
        'setSearchGames'
    ]))
)(SearchBare);

componentService.registerWebContentComponents({ GlobalSearch });
